// Client-side beeldverwerking vóór upload. De berichten-foto-route ontvangt
// base64 in een JSON-body en Vercel limiteert die tot ~4,5MB (base64 = +33%).
// Telefoonfoto's (5-12MP) knallen daar overheen → "Upload mislukt". Daarom
// verkleinen we hier in de browser: ruim onder de limiet, prima voor foto's in
// een berichten-thread, en kleinere opslag. Spiegelt de portal-helper.

export interface UploadImage {
  filename: string;
  contentType: string;
  /** Pure base64 (zonder data:-prefix). */
  base64: string;
}

const MAX_DIM = 2000; // langste zijde in px
const JPEG_QUALITY = 0.85;
/** Absolute bovengrens op het ORIGINEEL (sanity cap, vóór verkleinen). */
export const MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;

function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function resizeViaCanvas(file: File): Promise<UploadImage | null> {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return null;

  const scale = Math.min(1, MAX_DIM / Math.max(w, h));
  const targetW = Math.max(1, Math.round(w * scale));
  const targetH = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const outDataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const base64 = stripDataUrlPrefix(outDataUrl);
  if (!base64) return null;

  const filename = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return { filename, contentType: 'image/jpeg', base64 };
}

/**
 * Verkleint een afbeelding naar max MAX_DIM px (jpeg) zodat de upload-body klein
 * blijft. Lukt verkleinen niet (raar formaat), val terug op het origineel.
 */
export async function processImageForUpload(file: File): Promise<UploadImage> {
  try {
    const resized = await resizeViaCanvas(file);
    if (resized) return resized;
  } catch {
    // val terug op origineel
  }
  const base64 = stripDataUrlPrefix(await fileToDataUrl(file));
  return { filename: file.name, contentType: file.type || 'image/jpeg', base64 };
}
